import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { CameraStreamView } from './camera-stream-view'
import type { CameraViewMode } from './camera-stream-view'
import type { Camera } from './camera-types'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

interface Props {
    cameras: Camera[]
    mode: CameraViewMode
    /** Khung hình chỉ được kéo khi đang hiển thị (xem CameraStreamView). */
    active: boolean
    className?: string
    /** Bấm vào khung hình (vd mở xem trực tiếp camera đang hiện). */
    onOpen?: (camera: Camera) => void
}

/**
 * Khung hình của MỘT trong nhiều camera, kèm nút trước/sau và "1 / 3" khi có
 * hơn một camera. Dùng trên thẻ trạm bơm và trong hộp thoại bật bơm.
 *
 * Giữ id camera đang xem (không giữ vị trí): danh sách tải lại có thể đổi thứ
 * tự; id không còn thì quay về camera đầu.
 */
export function CameraSwitchView({
    cameras,
    mode,
    active,
    className,
    onOpen,
}: Props) {
    const [currentId, setCurrentId] = useState<string | null>(null)
    const found = cameras.findIndex((c) => c.id === currentId)
    const index = found === -1 ? 0 : found
    const camera = cameras.at(index)
    if (!camera) return null

    const total = cameras.length
    const go = (step: 1 | -1) =>
        setCurrentId(cameras[(index + step + total) % total].id)

    return (
        <div className={twJoin('relative', className)}>
            <CameraStreamView
                // Key theo camera: chuyển camera là đóng kết nối cũ trước.
                key={camera.id}
                camera={camera}
                active={active}
                mode={mode}
                className="size-full"
            />
            {onOpen && camera.has_stream && (
                <button
                    type="button"
                    aria-label={m.grower_area_camera_live_label({
                        name: camera.name,
                    })}
                    onClick={() => onOpen(camera)}
                    className="absolute inset-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
            )}
            <span className="pointer-events-none absolute bottom-2 left-2 max-w-[60%] truncate rounded-md bg-black/60 px-2 py-0.5 text-xs text-white">
                {camera.name}
            </span>
            {total > 1 && (
                <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-black/60 text-white">
                    <button
                        type="button"
                        aria-label={m.grower_area_camera_prev()}
                        onClick={() => go(-1)}
                        className="cursor-pointer rounded-md p-1 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        <ChevronLeft className="size-4" />
                    </button>
                    <span className="text-xs tabular-nums">
                        {m.grower_area_camera_position({
                            current: index + 1,
                            total,
                        })}
                    </span>
                    <button
                        type="button"
                        aria-label={m.grower_area_camera_next()}
                        onClick={() => go(1)}
                        className="cursor-pointer rounded-md p-1 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        <ChevronRight className="size-4" />
                    </button>
                </div>
            )}
        </div>
    )
}
